import React, { PureComponent } from 'react';
import clsx from 'clsx';
import {
  Theme,
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import SurveyItem from '../../util/SurveyItem';
import ChoiceOptions from '../../util/ChoiceOptions';
import { withSurveyContext, WithSurveyContextInjectedProps } from '../Contexts';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableGroupItem from './TableGroupItem';
import { Text } from '@lifeomic/chroma-react/components/Text';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import withIsMobile, {
  WithIsMobileInjectedProps,
} from '../../../../hoc/withIsMobile';
import TableGroupItemMobileVariant from './TableGroupItemMobileVariant';
import TableRowDetail from './TableRowDetail';

export const testIds = {
  tableGroup: (linkId: string) => getSurveyItemTestId(linkId),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(5),
    },
    // The default gap reads as a section break. A table that belongs to the
    // field above it (a one-row attestation under its own heading, say) needs
    // to sit close enough to be read as part of the same block.
    denseRoot: {
      marginTop: theme.spacing(2),
    },
    question: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    // A bold-titled matrix is being used as a section header, but bold grey
    // text alone does not separate it from the row text below. Full-contrast
    // text plus a thin rule mirrors the collapsible section header language
    // (grey band + 2px border) one level down. Untitled or non-bold matrix
    // headers keep the muted default.
    questionBold: {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      paddingBottom: theme.spacing(1),
    },
    cellHead: {
      color: theme.palette.text.secondary,
      padding: theme.spacing(2, 1),
      // Keep option columns compact and their headers unwrapped so they are
      // always fully visible; the label column absorbs the leftover width.
      whiteSpace: 'nowrap',
      width: '1%',
    },
    labelCellHead: {
      width: '100%',
    },
  });

export interface TableGroupOwnProps {
  surveyItem: SurveyItem;
}

export type TableGroupProps = WithStyles<typeof styles> &
  WithIsMobileInjectedProps &
  WithCheckForEnablementInjectedProps &
  TableGroupOwnProps &
  WithSurveyContextInjectedProps;

class TableGroup extends PureComponent<TableGroupProps> {
  choiceOptions: ChoiceOptions;
  rowItems: SurveyItem[];
  detailItemsByTrigger: Map<string, SurveyItem[]>;

  constructor(props: TableGroupProps) {
    super(props);

    // Children split into radio rows and row details (kit #s, notes, etc.
    // marked with the table-row-detail extension). Details render inside the
    // trigger row (or after the trigger block on mobile) rather than as
    // sibling table rows, so they stay visually grouped with their trigger.
    this.rowItems = props.surveyItem.subItems.filter(
      subItem => !subItem.isTableRowDetail,
    );
    const detailItems = props.surveyItem.subItems.filter(
      subItem => subItem.isTableRowDetail,
    );
    const rowLinks = new Set(this.rowItems.map(rowItem => rowItem.link));
    this.detailItemsByTrigger = new Map();
    for (const detailItem of detailItems) {
      // Details whose trigger is not a sibling row anchor to the last row so
      // they still render inside the table instead of disappearing.
      const trigger =
        detailItem.enablementTriggerLink &&
        rowLinks.has(detailItem.enablementTriggerLink)
          ? detailItem.enablementTriggerLink
          : this.rowItems[this.rowItems.length - 1]?.link;
      if (!trigger) continue;
      const existing = this.detailItemsByTrigger.get(trigger) || [];
      this.detailItemsByTrigger.set(trigger, [...existing, detailItem]);
    }

    const firstChoiceChild = this.rowItems[0];
    if (!firstChoiceChild) {
      throw new Error(
        'Items with the table group modifier extension must have choice sub items',
      );
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.choiceOptions = props.survey.getChoiceOptionsForItem(
      firstChoiceChild.link,
    );

    if (!this.choiceOptions) {
      throw new Error(
        'Items with the table group modifier extension must have choice sub items with options',
      );
    }
  }

  getDetailsForRow = (rowLink: string): SurveyItem[] =>
    this.detailItemsByTrigger.get(rowLink) || [];

  getHeaderText = () => {
    if (!this.props.surveyItem.hasText) return null;

    const { classes, surveyItem } = this.props;

    return (
      <Text
        size="body"
        weight={surveyItem.hasBoldTitle ? 'bold' : undefined}
        className={clsx(
          classes.question,
          surveyItem.hasBoldTitle && classes.questionBold,
        )}
      >
        {surveyItem.text}
      </Text>
    );
  };

  render() {
    const { surveyItem, classes, isEnabled, isMobile } = this.props;

    if (!isEnabled) return null;

    return (
      <div
        className={clsx(
          classes.root,
          surveyItem.hasDenseMargins && classes.denseRoot,
        )}
        {...getTestProps(testIds.tableGroup(surveyItem.link))}
      >
        {this.getHeaderText()}
        {isMobile && !surveyItem.shouldForceMatrixTableVariant ? (
          this.rowItems.map(rowItem => (
            <React.Fragment key={rowItem.link}>
              <TableGroupItemMobileVariant
                surveyItem={rowItem}
                choiceOptions={this.choiceOptions}
              />
              {/* Details hide themselves via their own enablement check */}
              {this.getDetailsForRow(rowItem.link).map(detailItem => (
                <TableRowDetail key={detailItem.link} surveyItem={detailItem} />
              ))}
            </React.Fragment>
          ))
        ) : (
          <Table role="table">
            <TableHead>
              <TableRow>
                <TableCell classes={{ root: classes.labelCellHead }} />
                {this.choiceOptions.options.map((option, index) => (
                  <TableCell
                    key={index}
                    classes={{ root: classes.cellHead }}
                    align="center"
                  >
                    {option.display}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.rowItems.map(rowItem => (
                <TableGroupItem
                  key={rowItem.link}
                  surveyItem={rowItem}
                  choiceOptions={this.choiceOptions}
                  detailItems={this.getDetailsForRow(rowItem.link)}
                  useCheckIcons={surveyItem.hasTableCheckIcons}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }
}

export default withIsMobile(
  withStyles(styles)(withSurveyContext(withCheckForEnablement(TableGroup))),
);
